import { useMemo } from 'react';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';

/**
 * Custom hook to manage dashboard data selection and revenue labels
 * @param {Object} user - The current user object
 * @param {Object} widgetData - Admin dashboard data
 * @param {Object} agencyDetail - Agency-specific data
 * @param {boolean} isAgencyDashboard - Whether this is an agency dashboard
 * @returns {Object} Dashboard data and labels
 */
const useDashboardData = (user, widgetData, agencyDetail, isAgencyDashboard) => {
  // Memoize revenue labels to prevent recreation on every render
  const revenueLabels = useMemo(() => {
    const isAdmin = user?.role === UserRole.ADMIN;

    if (isAdmin) {
      return {
        title: LanguageConverterV2('dashboard.widget.admin.revenue.title'),
        totalRevenue: LanguageConverterV2('dashboard.widget.admin.revenue.total'),
        scheduled: LanguageConverterV2('dashboard.widget.admin.revenue.booked'),
      };
    }

    return {
      title: LanguageConverterV2('dashboard.widget.agency.revenue.title'),
      availableRevenue: LanguageConverterV2('dashboard.widget.agency.revenue.available'),
      totalRevenue: LanguageConverterV2('dashboard.widget.agency.revenue.total'),
      totalRevenueDescription: LanguageConverterV2(
        'dashboard.widget.agency.revenue.total.description'
      ),
      scheduled: LanguageConverterV2('dashboard.widget.agency.revenue.scheduled'),
    };
  }, [user?.role]);

  // Memoize dashboard data selection
  const dashboardData = useMemo(() => {
    if (isAgencyDashboard) {
      return (agencyDetail?.dashboardData && agencyDetail.dashboardData) || {};
    }
    return widgetData || {};
  }, [isAgencyDashboard, agencyDetail?.dashboardData, widgetData]);

  // Destructure the data
  const { workers, revenue, futurePlacementsData } = dashboardData;

  return {
    revenueLabels,
    workers,
    revenue,
    futurePlacementsData,
  };
};

export default useDashboardData;
